const DEFAULT_PRECISION = 4;

export const DEFAULT_SOURCE_SHEETS = [
  { width: 60, height: 50 },
  { width: 61, height: 86 },
  { width: 63, height: 88 },
  { width: 65, height: 95 },
  { width: 72, height: 102 },
  { width: 72, height: 92 },
  { width: 74, height: 110 },
  { width: 82, height: 118 },
];

export const DEFAULT_MACHINE_CONSTRAINTS = {
  maxWidth: 72,
  maxHeight: 52,
  minWidth: 40,
  minHeight: 30,
  allowRotation: false,
  step: 1,
};

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function round(value, precision = DEFAULT_PRECISION) {
  const factor = 10 ** precision;
  return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
}

function decimalPlaces(value) {
  const text = String(value);
  if (!text.includes('.')) return 0;
  return text.split('.')[1].length;
}

function makeLabel(width, height) {
  return `${round(width)}x${round(height)}`;
}

function normalizeSheet(sheet) {
  if (typeof sheet === 'string') {
    const parts = sheet.toLowerCase().replace(/\s/g, '').split('x');
    return {
      width: toNumber(parts[0]),
      height: toNumber(parts[1]),
    };
  }

  return {
    ...sheet,
    width: toNumber(sheet?.width),
    height: toNumber(sheet?.height),
  };
}

function normalizeConstraints(machineConstraints = {}) {
  const get = (camel, snake, fallback) => (
    machineConstraints[camel] ?? machineConstraints[snake] ?? fallback
  );

  return {
    maxWidth: toNumber(get('maxWidth', 'max_width', DEFAULT_MACHINE_CONSTRAINTS.maxWidth)),
    maxHeight: toNumber(get('maxHeight', 'max_height', DEFAULT_MACHINE_CONSTRAINTS.maxHeight)),
    minWidth: toNumber(get('minWidth', 'min_width', DEFAULT_MACHINE_CONSTRAINTS.minWidth)),
    minHeight: toNumber(get('minHeight', 'min_height', DEFAULT_MACHINE_CONSTRAINTS.minHeight)),
    allowRotation: Boolean(get('allowRotation', 'allow_rotation', DEFAULT_MACHINE_CONSTRAINTS.allowRotation)),
    step: Math.max(0.01, toNumber(get('step', 'step', DEFAULT_MACHINE_CONSTRAINTS.step), 1)),
  };
}

function buildSteppedValues(min, max, step) {
  if (max < min) return [];

  const precision = Math.max(decimalPlaces(min), decimalPlaces(max), decimalPlaces(step));
  const scale = 10 ** precision;
  const scaledStep = Math.max(1, Math.round(step * scale));
  const start = Math.ceil((min * scale) / scaledStep) * scaledStep;
  const end = Math.floor(max * scale);
  const values = [];

  for (let current = start; current <= end; current += scaledStep) {
    values.push(round(current / scale));
  }

  return values;
}

function getMachineAcceptance(width, height, constraints) {
  const {
    maxWidth,
    maxHeight,
    minWidth,
    minHeight,
    allowRotation,
  } = constraints;

  const meetsMinimum = width >= minWidth && height >= minHeight;
  const fitsNormal = width <= maxWidth && height <= maxHeight;
  const fitsRotated = allowRotation && width <= maxHeight && height <= maxWidth;

  if (!meetsMinimum) {
    return { valid: false, machineOrientation: null };
  }

  if (fitsNormal) {
    return { valid: true, machineOrientation: 'normal' };
  }

  if (fitsRotated) {
    return { valid: true, machineOrientation: 'rotated' };
  }

  return { valid: false, machineOrientation: null };
}

function calculateBestGrid(sourceWidth, sourceHeight, cutWidth, cutHeight) {
  const normalCols = Math.floor(sourceWidth / cutWidth);
  const normalRows = Math.floor(sourceHeight / cutHeight);
  const normalCount = normalCols * normalRows;

  const rotatedCols = Math.floor(sourceWidth / cutHeight);
  const rotatedRows = Math.floor(sourceHeight / cutWidth);
  const rotatedCount = rotatedCols * rotatedRows;

  if (rotatedCount > normalCount) {
    return {
      columns: rotatedCols,
      rows: rotatedRows,
      sheetCount: rotatedCount,
      cuttingOrientation: 'rotated-on-source',
    };
  }

  return {
    columns: normalCols,
    rows: normalRows,
    sheetCount: normalCount,
    cuttingOrientation: 'normal-on-source',
  };
}

export class SheetCutOptimizer {
  calculateBestCuts(sourceSheets = DEFAULT_SOURCE_SHEETS, machineConstraints = DEFAULT_MACHINE_CONSTRAINTS) {
    const constraints = normalizeConstraints(machineConstraints);
    const sheets = sourceSheets.map(normalizeSheet).filter(sheet => sheet.width > 0 && sheet.height > 0);
    const results = [];
    const seen = new Set();

    sheets.forEach((sourceSheet) => {
      const sourceArea = sourceSheet.width * sourceSheet.height;
      const maxCandidateWidth = Math.min(
        sourceSheet.width,
        constraints.allowRotation ? Math.max(constraints.maxWidth, constraints.maxHeight) : constraints.maxWidth
      );
      const maxCandidateHeight = Math.min(
        sourceSheet.height,
        constraints.allowRotation ? Math.max(constraints.maxWidth, constraints.maxHeight) : constraints.maxHeight
      );
      const widths = buildSteppedValues(constraints.minWidth, maxCandidateWidth, constraints.step);
      const heights = buildSteppedValues(constraints.minHeight, maxCandidateHeight, constraints.step);

      widths.forEach((cutWidth) => {
        heights.forEach((cutHeight) => {
          const acceptance = getMachineAcceptance(cutWidth, cutHeight, constraints);
          if (!acceptance.valid) return;

          const grid = calculateBestGrid(sourceSheet.width, sourceSheet.height, cutWidth, cutHeight);
          if (grid.sheetCount <= 0) return;

          const usedArea = grid.sheetCount * cutWidth * cutHeight;
          if (usedArea > sourceArea + 0.0001) return;

          const key = `${sourceSheet.width}x${sourceSheet.height}|${cutWidth}x${cutHeight}`;
          if (seen.has(key)) return;
          seen.add(key);

          const wasteArea = round(sourceArea - usedArea);
          const wastePercent = round((wasteArea / sourceArea) * 100);
          const efficiencyPercent = round(100 - wastePercent);

          results.push({
            id: `${makeLabel(sourceSheet.width, sourceSheet.height)}__${makeLabel(cutWidth, cutHeight)}`,
            sourceSheet: {
              ...sourceSheet,
              label: sourceSheet.label || makeLabel(sourceSheet.width, sourceSheet.height),
            },
            generatedSheet: {
              width: cutWidth,
              height: cutHeight,
              label: makeLabel(cutWidth, cutHeight),
            },
            sheetCount: grid.sheetCount,
            wasteArea,
            wastePercent,
            efficiencyPercent,
            cutPattern: {
              columns: grid.columns,
              rows: grid.rows,
              cuttingOrientation: grid.cuttingOrientation,
              machineOrientation: acceptance.machineOrientation,
            },
          });
        });
      });
    });

    return results.sort((a, b) => (
      a.wastePercent - b.wastePercent
      || b.sheetCount - a.sheetCount
      || (b.generatedSheet.width * b.generatedSheet.height) - (a.generatedSheet.width * a.generatedSheet.height)
      || a.sourceSheet.label.localeCompare(b.sourceSheet.label)
      || a.generatedSheet.label.localeCompare(b.generatedSheet.label)
    ));
  }
}

export function calculateBestSheetCuts(sourceSheets, machineConstraints) {
  return new SheetCutOptimizer().calculateBestCuts(sourceSheets, machineConstraints);
}
